// Shared recurring-task logic used by App.js (hover-card deletes), Dashboard,
// and PlannerApp. Single source of truth — this logic was previously
// duplicated byte-for-byte in App.js and PlannerApp.js.
import { format, subDays } from 'date-fns';

// Todoist-style overdue collapsing: a neglected recurring task shows ONE
// overdue entry (its most recent missed occurrence), never a pile of copies.
// Standalone tasks pass through untouched. Older missed instances stay in the
// DB and still render on their own (past) dates in week/month views.
export function collapseOverdueRecurring(overdueTasks) {
  const latestBySeries = new Map();
  const standalone = [];
  for (const t of overdueTasks) {
    const seriesId = t.parent_task_id || (t.is_recurring ? t.id : null);
    if (!seriesId) { standalone.push(t); continue; }
    const existing = latestBySeries.get(seriesId);
    if (!existing || (t.date || '') > (existing.date || '')) latestBySeries.set(seriesId, t);
  }
  return [...standalone, ...latestBySeries.values()];
}

const stripDecorations = (t) => {
  const { isOverdue, originalDate, ...row } = t;
  return row;
};

// Delete a recurring series with scope 'single' | 'future' | 'all'.
// Returns everything needed to undo: the removed rows, any recurrence cap
// applied, and a predicate for pruning client state.
export async function deleteRecurringSeries({ supabase, task, scope, allTasks }) {
  const parentId = task.parent_task_id || task.id;

  if (scope === 'all') {
    const removedRows = allTasks
      .filter(t => t.id === parentId || t.parent_task_id === parentId)
      .map(stripDecorations);
    const { error: childErr } = await supabase.from('tasks').delete().eq('parent_task_id', parentId);
    if (childErr) throw childErr;
    const { error: parentErr } = await supabase.from('tasks').delete().eq('id', parentId);
    if (parentErr) throw parentErr;
    // Verify the parent row is actually gone — a swallowed/raced delete here
    // left orphan parents that reappeared after reload. Retry once if needed.
    try {
      const { data: leftover } = await supabase.from('tasks').select('id').eq('id', parentId).maybeSingle();
      if (leftover) {
        const { error: retryErr } = await supabase.from('tasks').delete().eq('id', parentId);
        if (retryErr) throw retryErr;
      }
    } catch (verifyErr) {
      if (verifyErr?.message) throw verifyErr;
    }
    return {
      removedRows,
      capInfo: null,
      shouldRemoveFromState: (t) => t.id === parentId || t.parent_task_id === parentId,
    };
  }

  if (scope === 'future') {
    const isParent = task.id === parentId;
    const removedRows = allTasks
      .filter(t =>
        (t.parent_task_id === parentId && t.date >= task.date) ||
        (isParent && t.id === parentId))
      .map(stripDecorations);
    const { error: futureErr } = await supabase.from('tasks').delete()
      .eq('parent_task_id', parentId)
      .gte('date', task.date);
    if (futureErr) throw futureErr;
    let capInfo = null;
    if (isParent) {
      // Deleting the parent itself — remove the row entirely. Past children
      // are untouched; cron won't regenerate because the parent is gone.
      await supabase.from('tasks').delete().eq('id', parentId);
    } else {
      // Deleting a child from the middle — cap the series so cron stops
      // extending, but keep the parent (and past children) as history.
      const parentRow = allTasks.find(t => t.id === parentId);
      const capDate = format(subDays(new Date(task.date + 'T00:00:00'), 1), 'yyyy-MM-dd');
      await supabase.from('tasks').update({ recurrence_end_date: capDate }).eq('id', parentId);
      capInfo = { parentId, prevEndDate: parentRow?.recurrence_end_date || null };
    }
    return {
      removedRows,
      capInfo,
      shouldRemoveFromState: (t) =>
        (t.parent_task_id === parentId && t.date >= task.date) || (isParent && t.id === parentId),
    };
  }

  // 'single'
  const { error } = await supabase.from('tasks').delete().eq('id', task.id);
  if (error) throw error;
  return {
    removedRows: [stripDecorations(task)],
    capInfo: null,
    shouldRemoveFromState: (t) => t.id === task.id,
  };
}

// Undo for deleteRecurringSeries: re-insert parents first (children reference
// them), then children in batches, then restore any recurrence cap.
export async function restoreRecurringSeries({ supabase, removedRows, capInfo }) {
  const parents = removedRows.filter(r => !r.parent_task_id);
  const children = removedRows.filter(r => r.parent_task_id);
  if (parents.length) {
    const { error } = await supabase.from('tasks').insert(parents);
    if (error) throw error;
  }
  for (let i = 0; i < children.length; i += 50) {
    const { error } = await supabase.from('tasks').insert(children.slice(i, i + 50));
    if (error) throw error;
  }
  if (capInfo) {
    await supabase.from('tasks')
      .update({ recurrence_end_date: capInfo.prevEndDate })
      .eq('id', capInfo.parentId);
  }
  return removedRows;
}
